import { PageIcon } from "@/components/PageIcon";
import React, { useEffect, useState, useCallback } from "react";
import { Inbox, Loader2, Truck, CheckCircle2, PackagePlus } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const dt = (s) => (s ? new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export default function StockRequestsPage() {
  const { can } = useAuth();
  const canWrite = can("stock.adjust") || can("stock.manage");
  const [status, setStatus] = useState("open");
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    api.get("/stock-requests", { params: { status } })
      .then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0, open_total: 0 }));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const fulfil = async (sr) => {
    setBusyId(sr.request_id);
    try {
      const r = await api.post(`/product-requests/${sr.request_id}/fulfil-from-warehouse`);
      toast.success(r?.data?.note || "Fulfilled from warehouse");
      load();
    }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not fulfil"); }
    finally { setBusyId(null); }
  };

  const receive = async (sr) => {
    setBusyId(sr.request_id);
    try {
      const r = await api.post(`/product-requests/${sr.request_id}/receive-stock`);
      toast.success(r?.data?.note || "Goods received into warehouse");
      load();
    }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not receive goods"); }
    finally { setBusyId(null); }
  };

  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="stock-requests-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <PageIcon />
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Stock Requests</h1>
            <p className="text-sm text-slate-500">Open requests raised against jobs for the warehouse to action.</p>
          </div>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="sr-status-toggle">
          {[["open", "Open"], ["all", "All"]].map(([v, l]) => (
            <button key={v} onClick={() => setStatus(v)} data-testid={`sr-status-${v}`}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${status === v ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 bg-slate-50/70 border-b border-slate-100">
              <th className="py-2.5 px-4">Product</th>
              <th className="py-2.5 px-2 text-right">Qty</th>
              <th className="py-2.5 px-2 text-right">Reserved</th>
              <th className="py-2.5 px-4">From</th>
              <th className="py-2.5 px-4">Job</th>
              <th className="py-2.5 px-4">Requested By</th>
              <th className="py-2.5 px-4">When</th>
              <th className="py-2.5 px-4">Status</th>
              <th className="py-2.5 px-4 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {!data ? (
              <tr><td colSpan={9} className="py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan={9} className="py-10 text-center text-slate-400">No stock requests {status === "open" ? "outstanding" : "found"}.</td></tr>
            ) : data.items.map((sr) => (
              <tr key={sr.sr_id} className="hover:bg-slate-50/70" data-testid={`sr-row-${sr.request_id}`}>
                <td className="py-2.5 px-4 font-medium text-slate-800">{sr.product}</td>
                <td className="py-2.5 px-2 text-right tabular-nums">{sr.qty}</td>
                <td className="py-2.5 px-2 text-right tabular-nums text-slate-500">{sr.reserved}</td>
                <td className="py-2.5 px-4 text-slate-600">{sr.location_name}</td>
                <td className="py-2.5 px-4">{sr.task_legacy_id ? <a href={`/tasks/${sr.task_legacy_id}`} className="text-indigo-600 hover:underline">{sr.requested_for || `#${sr.task_legacy_id}`}</a> : (sr.requested_for || "—")}</td>
                <td className="py-2.5 px-4 text-slate-600">{sr.requested_by}</td>
                <td className="py-2.5 px-4 text-xs text-slate-400">{dt(sr.requested_at)}</td>
                <td className="py-2.5 px-4">
                  {sr.status === "Fulfilled"
                    ? <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700"><CheckCircle2 className="w-3 h-3" /> Fulfilled</span>
                    : <span className="inline-flex items-center rounded-full border border-amber-300 bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800">Requested</span>}
                </td>
                <td className="py-2.5 px-4 text-right">
                  {canWrite && sr.status !== "Fulfilled" && (
                    <div className="inline-flex gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => receive(sr)} disabled={busyId === sr.request_id}
                        className="h-8 gap-1.5" data-testid={`sr-receive-${sr.request_id}`}>
                        <PackagePlus className="w-3.5 h-3.5" /> Receive
                      </Button>
                      <Button size="sm" onClick={() => fulfil(sr)} disabled={busyId === sr.request_id}
                        className="h-8 gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid={`sr-fulfil-${sr.request_id}`}>
                        {busyId === sr.request_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Truck className="w-3.5 h-3.5" />} Fulfil to van
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
